import { defineStore } from 'pinia'
import { ComputedRef, Ref, computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { ToastOptions, useToastStore } from '@/components_new/Toaster/store'
import { useTr } from '@/composables/use-tr'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import {
    isBrowserFirefox,
    isBrowserIOSChrome,
    isBrowserIOSSafari,
    isDesktopChrome,
    isDesktopEdge,
    isDesktopSafari,
} from '@/utils/device'

import ChromeDesktopSVG from './guides/chrome-desktop.svg?file'

const PWA_INSTALL_CONFIG = {
    MIN_VISITS: 2,
    MAX_DISPLAY_COUNT: 100,
    DISPLAY_DELAY_HOURS: 24 * 7,
} as const

const msStoreGameIds: Record<string, string> = {
    'moto-x3m': '9pfc45g1xhp7',
    'hidden-object-clues-and-mysteries': '9ndwwpl265t4',
    'fashion-princess--dress-up-for-girls-e00c-1': '9mvfcjf74zfn',
    'age-of-tanks-warriors-td-war': '9nxv05zxpkq7',
    'home-design-decorate-house': '9n403rx37m6x',
    'sprunki-world-online-rp--play-with-friends': '9mx9nlhd6xmm',
    'speed-per-click-obby': '9pbp2j1pt8rd',
    'mr-racer-car-racing': '9n08dpggwvqh',
    'hazmob-fps-online-shooter': '9n7qs1kn0v8l',
    'motorcycle-racer-road-mayhem': '9mwr95hfg2zg',
    'playground-man-ragdoll-show': '9nj10wf288jp',
}

export interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export const usePWAInstallStore = defineStore(
    'pwa_install',
    () => {
        const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
        // set boolean flag to prevent re-rendering components
        const hasDeferredPrompt = ref<boolean>(false)
        const toastStore = useToastStore()
        const layoutStore = useLayoutStore()
        const loggerStore = useLoggerStore()
        const tr = useTr()

        const displayCount = ref(0)
        const showingNativePWA = ref(false)
        const lastDisplayTime = ref(0)
        const optedOut = ref(false)
        const installed = ref(false)
        const installedGames: Ref<Record<string, boolean>> = ref({})
        const sessions = ref(0)
        const initialized = ref(false)
        const isDisplaying = ref(false)
        const pwaHasShowed = ref(false)

        const route = useRoute()
        const isGamePage = computed(() => route.name === ROUTE_NAMES.GAME_PAGE)
        const appStore = useAppStore()
        const isDesktop = computed(() => appStore.platform === 'desktop')

        const hru: ComputedRef<string | undefined> = computed(() => {
            return route.params.game as string
        })

        const checkInstalled = computed(() => {
            if (isGamePage.value && hru.value) {
                if (installedGames.value[hru.value]) {
                    return true
                }
            } else {
                return installed.value
            }
            return false
        })

        const setInstalled = () => {
            if (isGamePage.value && hru.value) {
                installedGames.value = { ...installedGames.value, [hru.value]: true }
            } else {
                installed.value = true
            }
        }

        const shouldShowPrompt = computed(() => {
            if (!isDesktop.value) {
                return false
            }

            if (checkInstalled.value) {
                return false
            }

            if (layoutStore.fullScreen) {
                return false
            }

            if (isBrowserFirefox()) {
                return false
            }

            // keep showing the toast for now
            // if (optedOut.value) {
            //     return false
            // }
            if (sessions.value < PWA_INSTALL_CONFIG.MIN_VISITS) {
                return false
            }

            if (displayCount.value >= PWA_INSTALL_CONFIG.MAX_DISPLAY_COUNT) {
                return false
            }

            if (Date.now() - lastDisplayTime.value < PWA_INSTALL_CONFIG.DISPLAY_DELAY_HOURS * 60 * 60 * 1000) {
                return false
            }

            return true
        })

        const updateInstallPWAConfig = () => {
            lastDisplayTime.value = Date.now()
            displayCount.value += 1
        }

        const handleOptOut = (force = false) => {
            optedOut.value = true
            isDisplaying.value = false
            // do not count for game install pwa as it shows very often
            if (!force) {
                updateInstallPWAConfig()
            }
        }

        const handleOnShow = (force = false) => {
            loggerStore.logInterfaceEvents({
                interface_target: 'pwa_portal_install_prompt_shown_toast',
                eventName: 'interface_show',
            })
            if (!force) {
                updateInstallPWAConfig()
            }
        }

        const handleInstalled = () => {
            loggerStore.logEvent({
                event: 'custom_event',
                eventName: 'pwa_native_install_accepted',
            })
            setInstalled()
            deferredPrompt.value = null
            updateInstallPWAConfig()
            const startURL = document.querySelector<HTMLLinkElement>('link[rel=manifest]')?.dataset.start_url
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            // Chrome transparently moves current runtime into standalone mode, but it doesn't make it PWA until we reload it with proper URL with clid
            if (startURL && isStandalone) {
                location.href = startURL
            }
        }

        const handleAccepted = () => {
            if (deferredPrompt.value) {
                triggerNativePrompt()
            }

            // We show button only for native prompt atm
            // else {
            //     handleInstalled()
            // }

            isDisplaying.value = false
        }

        const triggerNativePrompt = async () => {
            if (deferredPrompt.value) {
                try {
                    showingNativePWA.value = true
                    await deferredPrompt.value.prompt()

                    loggerStore.logInterfaceEvents({
                        interface_target: 'pwa_native_install_prompt_shown',
                        eventName: 'interface_show',
                    })
                    const { outcome } = await deferredPrompt.value.userChoice

                    if (outcome === 'accepted') {
                        handleInstalled()
                    } else {
                        handleOptOut()
                    }
                    showingNativePWA.value = false
                    pwaHasShowed.value = true
                } catch {
                    handleOptOut()
                }
            }
        }

        const showPWAInstallPrompt = async (settings?: {
            force?: boolean
            showTimeoutMs?: number
            isFullScreenAllowed?: boolean
        }) => {
            const { force, showTimeoutMs, isFullScreenAllowed } = settings || {}
            if (checkInstalled.value || isBrowserFirefox() || appStore.isAppEnv) {
                return
            }
            if (!force && !shouldShowPrompt.value) {
                return
            }

            // loading animations when showing the toast
            let animation, image
            if (isDesktopChrome() || isDesktopEdge()) {
                image = ChromeDesktopSVG
            } else if (isBrowserIOSSafari() || isBrowserIOSChrome()) {
                animation = await import('./guides/safari.json').then((module) => module.default)
            } else if (isDesktopSafari()) {
                animation = await import('./guides/safari-desktop.json').then((module) => module.default)
            } else {
                animation = await import('./guides/chromium.json').then((module) => module.default)
            }
            const description = isDesktopSafari()
                ? tr('toast-pwa.description-safari-desktop')
                : tr('toast-pwa.description')
            const buttonText = hasDeferredPrompt.value ? tr('toast-pwa.button_text') : undefined

            isDisplaying.value = true
            pwaHasShowed.value = true

            const params: ToastOptions = {
                type: 'pwa-prompt',
                title: tr('toast-pwa.title'),
                description,
                animation,
                image,
                buttonText,
                showTimeoutMs,
                priority: 1,
                imageStyles: { height: '120rem' },
                onAccept: handleAccepted,
                onClose: () => handleOptOut(force),
                onShow: () => handleOnShow(force),
                isFullScreenAllowed: isFullScreenAllowed ?? force,
            }
            toastStore.showToast(params)
        }

        const showPWAGameInstallPrompt = (gamePic: string | undefined) => {
            if (checkInstalled.value || isBrowserFirefox()) {
                return
            }
            const image = gamePic || ChromeDesktopSVG // fallback
            isDisplaying.value = true
            pwaHasShowed.value = true
            const params: ToastOptions = {
                type: 'pwa-prompt-game',
                title: tr('toast-pwa.game_title'),
                description: tr('toast-pwa.game_description'),
                image,
                showTimeoutMs: 40_000, // 40 sec
                buttonText: tr('toast-pwa.button_game_text'),
                priority: 2,
                onAccept: () => {
                    triggerNativePrompt()
                    isDisplaying.value = false
                },
                onClose: () => {
                    optedOut.value = true
                    isDisplaying.value = false
                },
                onShow: () => {
                    loggerStore.logInterfaceEvents({
                        interface_target: 'pwa_portal_install_game_prompt_shown_toast',
                        eventName: 'interface_show',
                    })
                },
            }

            toastStore.showToast(params)
        }

        const forceHidePWAInstallPrompt = () => {
            if (toastStore.toast?.type === 'pwa-prompt' || toastStore.toast?.type === 'pwa-prompt-game') {
                toastStore.closeToast()
            }
        }

        async function install() {
            if (deferredPrompt.value) {
                await triggerNativePrompt()
            } else {
                await showPWAInstallPrompt({ force: true })
            }
        }

        function init() {
            if (initialized.value) {
                return
            }

            initialized.value = true
            sessions.value += 1

            if (appStore.isAppEnv) {
                setInstalled()
                return
            }

            window.addEventListener('beforeinstallprompt', (e: Event) => {
                if (appStore.platform === 'android' && appStore.isPortalEnv && !appStore.isAppEnv) {
                    //when we are in android, we know that manifest is for Google Play
                    loggerStore.logInterfaceEvents({
                        interface_target: 'native_app_smart_install_shown_android',
                        eventName: 'interface_show',
                    })
                } else {
                    e.preventDefault()
                    deferredPrompt.value = e as BeforeInstallPromptEvent
                    hasDeferredPrompt.value = true
                }
            })

            window.addEventListener('appinstalled', () => {
                handleInstalled()
            })
        }

        async function onInstallPWA() {
            if (hasDeferredPrompt.value) {
                forceHidePWAInstallPrompt()
                await triggerNativePrompt()
            } else {
                await showPWAInstallPrompt({ force: true })
            }
        }

        async function onInstallPWAorMSN() {
            const url = msStoreGameIds[hru.value as string]
            if (navigator.userAgentData?.platform === 'Windows' && url) {
                loggerStore.logEvent({
                    event: 'custom_event',
                    eventName: 'ms_store_url_opened',
                })
                window.open(`https://apps.microsoft.com/detail/${url}?mode=full`, '_blank')
            } else {
                await onInstallPWA()
            }
        }

        return {
            displayCount,
            lastDisplayTime,
            optedOut,
            installed,
            sessions,
            triggerNativePrompt,
            handleOptOut,
            handleAccepted,
            handleOnShow,
            handleInstalled,
            showPWAInstallPrompt,
            showPWAGameInstallPrompt,
            forceHidePWAInstallPrompt,
            hasDeferredPrompt,
            shouldShowPrompt,
            init,
            initialized,
            isDisplaying,
            install,
            showingNativePWA,
            pwaHasShowed,
            installedGames,
            setInstalled,
            checkInstalled,
            onInstallPWAorMSN,
        }
    },
    {
        persist: {
            key: 'pwa_install',
            pick: ['displayCount', 'lastDisplayTime', 'optedOut', 'installed', 'sessions', 'installedGames'],
        },
    },
)
